import { buildWaiter } from "@ember/test-waiters";
import Ember from "ember";
import { cell, resource, resourceFactory } from "ember-resources";

const rAF = requestAnimationFrame;
const waiter = buildWaiter("incremental-list-waiter");

function incrementalList<Item>(
  /**
   * The full items list.
   * This list will be incrementally reconstructed in batches and yielded
   * */
  items: Iterable<Item>,

  /**
   * The number of items we add on each iteration.
   *
   * Lower this number when the block provided to this component gets harder to render
   * */
  chunkSize: number,

  options?: {
    /**
     * Should we disable the incremental construction of the visibleItems object
     * and directly return the provided "items" argument
     *
     * This defaults to Ember.testing since we don't really want
     * the incremental behaviour in this context
     */
    disabled?: boolean;

    /**
     * Callback called when the incremental-list has finished building the array
     *
     * defaults to an empty function
     * */
    onComplete?: () => void;
  }
) {
  let token;
  const { disabled = Ember.testing, onComplete = () => {} } = options ?? {};

  /**
   * The responsive array containing the items to display
   */
  const visibleItems = cell([] as Item[]);

  const iterator = items[Symbol.iterator]();

  /**
   * The animationFrameId kept for reference
   * If the resource is disposed of before its loop was not finished, we want to make
   * sure the animationFrame will be canceled
   */
  let animationFrameId: number;

  const loop = () => {
    let result;
    const nextBatch: Item[] = [];

    // Use the iterator to construct the next batch of items
    for (let i = 0; i < chunkSize; i++) {
      result = iterator.next();
      if (result.done) break; // Stop if iterator is exhausted
      nextBatch.push(result.value);
    }

    // 🛑 Stop the loop if the next batch is empty
    if (nextBatch.length == 0) {
      // trigger the "onComplete" callback if provided
      onComplete();

      // 🛑 Now that the list is fully rendered, we can stop the test waiter
      waiter.endAsync(token);

      return;
    }

    // ⏱️ Schedule the modification of `visibleItems` in an animation frame
    // to keep the UI responsive
    animationFrameId = rAF(() => {
      // update the 'visibleItems' cell
      visibleItems.set([...visibleItems.current, ...nextBatch]);

      // ⏱️ Schedule the loop for the next frame
      // this rAF call is nested inside another one to make sure
      // we don't call the loop before the modification of `visibleItems`
      // is finished
      animationFrameId = rAF(loop);
    });
  };

  return resource(({ on }) => {
    if (disabled) {
      visibleItems.set(items as Item[]);
    } else {
      token = waiter.beginAsync();
      loop();

      on.cleanup(() => {
        cancelAnimationFrame(animationFrameId);
      });
    }

    return () => visibleItems.current;
  });
}

resourceFactory(incrementalList);

export default incrementalList;
